import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { BarChart3, AlertCircle, CheckCircle2, Users, MessageSquare, ThumbsUp, ThumbsDown, FlaskConical, Milestone, CalendarDays, Sparkles, AlertTriangle, ArrowRight } from "lucide-react";
import { format, parseISO, isBefore, isToday } from "date-fns";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeConfig = {
  requirement: { label: "Requirement", color: "text-purple-600 dark:text-purple-400", bg: "bg-purple-50 dark:bg-purple-900/30", border: "border-purple-200 dark:border-purple-800", icon: Sparkles, panel: "tobe-enhancements" },
  uat: { label: "UAT", color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-50 dark:bg-amber-900/30", border: "border-amber-200 dark:border-amber-800", icon: FlaskConical, panel: "uat" },
  painpoint: { label: "Pain Point", color: "text-red-600 dark:text-red-400", bg: "bg-red-50 dark:bg-red-900/30", border: "border-red-200 dark:border-red-800", icon: AlertTriangle, panel: "painpoints" },
  milestone: { label: "Milestone", color: "text-teal-600 dark:text-teal-400", bg: "bg-teal-50 dark:bg-teal-900/30", border: "border-teal-200 dark:border-teal-800", icon: Milestone, panel: "milestones" },
};

export const DashboardPanel = ({ onNavigate }) => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [data, setData] = useState(null);
  const [myTasks, setMyTasks] = useState([]);

  useEffect(() => {
    const params = productId ? { product_id: productId } : {};
    axios.get(`${API}/dashboard`, { params }).then((r) => setData(r.data)).catch(console.error);
    axios.get(`${API}/my-calendar`, { params }).then((r) => setMyTasks(r.data)).catch(console.error);
  }, [productId]);

  if (!data) return <div className="flex items-center justify-center py-20 text-slate-400">Loading dashboard...</div>;

  const cards = [
    { label: "Stakeholders", value: data.stakeholders, icon: Users, color: "#00D4C8", bg: "bg-teal-50 dark:bg-teal-900/30", panel: "stakeholders" },
    { label: "Pain Points", value: `${data.painpoints.completed}/${data.painpoints.total}`, sub: "resolved", icon: AlertCircle, color: "#FF6B6B", bg: "bg-red-50 dark:bg-red-900/30", panel: "painpoints" },
    { label: "Requirements", value: `${data.requirements.completed}/${data.requirements.total}`, sub: "done", icon: CheckCircle2, color: "#39D98A", bg: "bg-green-50 dark:bg-green-900/30", panel: "tobe-enhancements" },
    { label: "UAT Items", value: `${data.uat.completed}/${data.uat.total}`, sub: "passed", icon: FlaskConical, color: "#A78BFA", bg: "bg-violet-50 dark:bg-violet-900/30", panel: "uat" },
    { label: "Completed", value: `${data.milestones.completed}/${data.milestones.total}`, sub: "milestones", icon: Milestone, color: "#3B82F6", bg: "bg-blue-50 dark:bg-blue-900/30", panel: "milestones" },
    { label: "In Progress", value: data.milestones.in_progress, sub: "milestones", icon: Milestone, color: "#22C55E", bg: "bg-green-50 dark:bg-green-900/30", panel: "milestones" },
    { label: "Delayed/At Risk", value: data.milestones.delayed, sub: "milestones", icon: Milestone, color: "#EF4444", bg: "bg-red-50 dark:bg-red-900/30", panel: "milestones" },
    { label: "Communications", value: data.feedback.total, sub: data.feedback.needs_response > 0 ? `${data.feedback.needs_response} needs response` : null, icon: MessageSquare, color: "#64748B", bg: "bg-slate-100 dark:bg-slate-800", alert: data.feedback.needs_response > 0 ? data.feedback.needs_response : 0, panel: "feedback" },
  ];

  const goPercent = data.gonogo.total > 0 ? Math.round((data.gonogo.go / data.gonogo.total) * 100) : 0;

  // Filter tasks: upcoming (not completed) sorted by due date, limit 5
  const upcomingTasks = myTasks
    .filter(t => {
      if (!t.due_date) return false;
      const done = ["Completed", "Passed", "Solution Completed"].includes(t.status);
      return !done;
    })
    .sort((a, b) => {
      try { return parseISO(a.due_date) - parseISO(b.due_date); } catch { return 0; }
    })
    .slice(0, 5);

  const overdueTasks = myTasks.filter(t => {
    if (!t.due_date) return false;
    try {
      const d = parseISO(t.due_date);
      return isBefore(d, new Date()) && !isToday(d) && !["Completed", "Passed", "Solution Completed"].includes(t.status);
    } catch { return false; }
  });

  return (
    <div data-testid="dashboard-panel" className="fade-up">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#00D4C8] mb-3">Product Overview</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">Dashboard</h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">{selectedProduct?.name || "Product Builder"} at a glance</p>

      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mb-8" data-testid="dashboard-cards">
        {cards.map((c, idx) => {
          const Icon = c.icon;
          return (
            <Card key={c.label} className="card-modern hover-lift relative overflow-hidden cursor-pointer" style={{ animationDelay: `${idx * 50}ms` }} onClick={() => onNavigate && onNavigate(c.panel)} data-testid={`dashboard-card-${c.panel}`}>
              {c.alert > 0 && (
                <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-red-500 to-red-600 text-white text-xs font-bold flex items-center justify-center shadow-lg" data-testid="feedback-alert-badge">
                  {c.alert}
                </div>
              )}
              <CardContent className="p-4 sm:p-5 flex items-start justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">{c.label}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 mt-1 font-['Outfit']">{c.value}</p>
                  {c.sub && <p className={`text-[10px] sm:text-xs mt-0.5 ${c.alert > 0 ? "text-red-500 font-semibold" : "text-slate-400 dark:text-slate-500"}`}>{c.sub}</p>}
                </div>
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${c.bg} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: c.color }} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* My Tasks Widget */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        <Card className="card-modern overflow-hidden" data-testid="my-tasks-widget">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-teal-400 to-teal-500 flex items-center justify-center">
                  <CalendarDays className="w-3.5 h-3.5 text-white" />
                </div>
                My Upcoming Tasks
              </h3>
              {overdueTasks.length > 0 && (
                <Badge className="bg-gradient-to-r from-red-500 to-red-600 text-white border-0 text-[10px] shadow-sm">{overdueTasks.length} Overdue</Badge>
              )}
            </div>
            {upcomingTasks.length === 0 ? (
              <p className="text-sm text-slate-400 dark:text-slate-500 py-4">No upcoming deadlines assigned to you</p>
            ) : (
              <div className="space-y-2">
                {upcomingTasks.map(task => {
                  const cfg = typeConfig[task.type] || typeConfig.requirement;
                  const Icon = cfg.icon;
                  let dateStr = "";
                  let isOverdue = false;
                  try {
                    const d = parseISO(task.due_date);
                    dateStr = format(d, "MMM d");
                    isOverdue = isBefore(d, new Date()) && !isToday(d);
                  } catch { dateStr = task.due_date; }
                  return (
                    <div key={task.id} onClick={() => onNavigate && onNavigate(cfg.panel)} className={`group flex items-center gap-3 p-3 rounded-xl border transition-all duration-200 cursor-pointer hover:shadow-sm ${isOverdue ? "bg-red-50/50 dark:bg-red-900/20 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/30" : "bg-slate-50/80 dark:bg-slate-800/50 border-slate-100 dark:border-slate-700 hover:bg-white dark:hover:bg-slate-800 hover:border-slate-200 dark:hover:border-slate-600"}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bg} shadow-sm group-hover:shadow transition-shadow`}>
                        <Icon className={`w-4 h-4 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate group-hover:text-slate-900 dark:group-hover:text-white transition-colors">{task.title}</p>
                        <p className="text-[10px] text-slate-400 dark:text-slate-500">{cfg.label} · {task.product_name}</p>
                      </div>
                      <span className={`text-[10px] font-bold flex-shrink-0 px-2 py-1 rounded-md ${isOverdue ? "bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300" : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300"}`}>{dateStr}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {myTasks.length > 5 && (
              <button onClick={() => onNavigate && onNavigate("calendar")} className="text-xs font-semibold text-[#00D4C8] hover:text-teal-700 dark:hover:text-teal-400 mt-4 flex items-center gap-1 transition-colors group" data-testid="view-all-tasks-btn">
                See all tasks <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
              </button>
            )}
          </CardContent>
        </Card>

        {/* Go/No-Go Summary */}
        <Card className="card-modern overflow-hidden cursor-pointer" data-testid="dashboard-gonogo" onClick={() => onNavigate && onNavigate("gonogo")}>
          <CardContent className="p-5 sm:p-6">
            <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center">
                <ThumbsUp className="w-3.5 h-3.5 text-white" />
              </div>
              Go / No-Go Status
            </h3>
            {data.gonogo.total > 0 ? (
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/30 px-3 py-2 rounded-xl">
                    <ThumbsUp className="w-4 h-4 text-green-500" />
                    <span className="text-xl font-bold text-slate-900 dark:text-slate-100 font-['Outfit']">{data.gonogo.go}</span>
                    <span className="text-xs text-slate-400 dark:text-slate-500">Go</span>
                  </div>
                  <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/30 px-3 py-2 rounded-xl">
                    <ThumbsDown className="w-4 h-4 text-red-500" />
                    <span className="text-xl font-bold text-slate-900 dark:text-slate-100 font-['Outfit']">{data.gonogo.nogo}</span>
                    <span className="text-xs text-slate-400 dark:text-slate-500">No Go</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden shadow-inner">
                    <div className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full transition-all duration-500" style={{ width: `${goPercent}%` }} />
                  </div>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1.5 font-medium">{goPercent}% approval</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-400 dark:text-slate-500">No votes yet. Add stakeholders and start voting in the Go/No-Go section.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
